import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { setIsBackground, setIsDebug, setNetworkConnected } from '../App';
import { logConsole, logTransaction } from '../common/log';
import { getStateDateAsString, getStateStringAsDate, handleThunkError } from '../common/util';
import { AttachmentAssetType, getEmptyAttachmentContainer } from '../models/dms/attachment.model';
import { IStockUnitDetails } from '../models/dms/stockUnit.model';
import { ITechnician } from '../models/dms/technician.model';
import { IWorkshopVehicleDetails } from '../models/dms/workshopVehicle.model';
import { LogType } from '../models/log.model';
import { setTechnicianStoreAsync } from './authSlice';
import { setBranchesProcessingStoreAsync, setBranchesSyncStoreAsync } from './branchSyncSlice';
import { setSelectedClockingHistoryStoreAsync } from './clockingHistorySlice';
import { setDelayCodesProcessingStoreAsync, setDelayCodesSyncStoreAsync } from './delayCodeSyncSlice';
import { setRepairOrderIsProcessingStoreAsync, setRepairOrderSyncStoreAsync } from './repairOrderSyncSlice';
import { clearRequestQueueStoreAsync } from './requestQueueSlice';
import { setDistanceTrackingUserActivatedStoreAsync, setDistanceTrackingUserPausedStoreAsync, setEnableOfflineModeStoreAsync, setGpsServicesUserEnabledStoreAsync } from './settingsSlice';
import { setSelectedStockUnitStoreAsync } from './stockUnitSlice';
import { setAttendanceClockingStoreAsync, setLabourClockingStoreAsync } from './technicianClockingSlice';
import { setAdhocCommentCacheStoreAsync, setSelectedTechnicianCommentsState, setSelectedTechnicianCommentsStoreAsync } from './technicianCommentSlice';
import { setAdhocAttachmentCacheStoreAsync, setSelectedVehicleAttachmentsStoreAsync } from './vehicleAttachmentSlice';
import { setWorkCategoriesProcessingStoreAsync, setWorkCategoriesSyncStoreAsync } from './workCategorySyncSlice';
import { setSelectedWorkshopVehicleStoreAsync } from './workshopVehicleSlice';
import { setFiltersOrSortsStoreAsync, setOrderByDateDescendingStoreAsync, setSelectedBranchCodeStoreAsync, setTechCodeCheckboxStoreAsync, setTodaysBookingsCheckboxStoreAsync } from './filterMenuSlice';
import { setMediaSettingsProcessingStoreAsync } from './mediaSettingsSyncSlice';
import { IClockingHistory } from '../models/dms/clocking.model';

interface IAppState {
  stateId: string;
  isInAction: boolean;

  allowFetchClockings: boolean;
  allowOverrideAttendance: boolean;
  allowOverrideLabour: boolean;
  clockingFetchCooldown: number;
  networkConnected: boolean;
  isBackground: boolean;

  //isLoadingData: boolean;
  isLoadingCount: number;
  isDebug: boolean;
  isDemo: boolean;
  demoDateTime: string;
  showMainMenu: boolean;
}

const initialState: IAppState = {
  stateId: '0',
  isInAction: false, // When a menu or modal is displayed, used for disabling automatic processes while performing actions

  allowFetchClockings: false, // App is allowed to fetch clockings from the server
  allowOverrideAttendance: false,
  allowOverrideLabour: false,
  clockingFetchCooldown: 0, // Add a delay that will countdown to zero. While the dalay is greater than zero, no clockings should sync from server,
  networkConnected: true,
  isBackground: false,

  //isLoadingData: false,
  isLoadingCount: 0,
  isDebug: false,
  isDemo: false,
  demoDateTime: getStateDateAsString(new Date()),
  showMainMenu: true,
};

/// THUNKS

export const resetAppStateAsync = createAsyncThunk<{}, { clearAll: boolean }>('app/resetAppStateAsync', async (payload, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Resetting app state - clearAll: ${payload.clearAll}`, '[app] resetAppStateAsync');
  try {
    dispatch(setAllowOverrideAttendanceState(true));
    dispatch(setAllowOverrideLabourState(true));
    dispatch(setAllowFetchClockingsState(true));
    dispatch(resetClockingFetchCooldownState());
    dispatch(setRepairOrderIsProcessingStoreAsync(false));
    dispatch(setDelayCodesProcessingStoreAsync(false));
    dispatch(setWorkCategoriesProcessingStoreAsync(false));
    dispatch(setBranchesProcessingStoreAsync(false));
    dispatch(setMediaSettingsProcessingStoreAsync(false));
    await dispatch(setSelectedClockingHistoryStoreAsync({} as Record<string, IClockingHistory[]>));

    if (payload.clearAll) {
      dispatch(setIsDebugState(false));
      dispatch(setIsInActionState(false));
      await dispatch(setEnableOfflineModeStoreAsync(true));
      await dispatch(setFiltersOrSortsStoreAsync(0));
      await dispatch(setSelectedBranchCodeStoreAsync(''));
      await dispatch(setTechCodeCheckboxStoreAsync(false));
      await dispatch(setTodaysBookingsCheckboxStoreAsync(false));
      await dispatch(setOrderByDateDescendingStoreAsync(false));
      await dispatch(setGpsServicesUserEnabledStoreAsync(true));

      // This will clear everything
      // Clear clocking state
      await dispatch(setAttendanceClockingStoreAsync(undefined));
      await dispatch(setLabourClockingStoreAsync(undefined));
      // Clear sync state
      await dispatch(setRepairOrderSyncStoreAsync(undefined));
      await dispatch(setWorkCategoriesSyncStoreAsync(undefined));
      await dispatch(setDelayCodesSyncStoreAsync(undefined));
      await dispatch(setBranchesSyncStoreAsync(undefined));

      // Clear request queue
      await dispatch(clearRequestQueueStoreAsync());
      // Clear other states
      await dispatch(setTechnicianStoreAsync({} as ITechnician));
      await dispatch(setSelectedClockingHistoryStoreAsync({} as Record<string, IClockingHistory[]>));
      await dispatch(setSelectedStockUnitStoreAsync({} as IStockUnitDetails));
      await dispatch(setSelectedTechnicianCommentsStoreAsync([]));
      await dispatch(setAdhocCommentCacheStoreAsync([]));
      await dispatch(setSelectedWorkshopVehicleStoreAsync({} as IWorkshopVehicleDetails));
      await dispatch(setAdhocAttachmentCacheStoreAsync([]));
      await dispatch(setSelectedVehicleAttachmentsStoreAsync(getEmptyAttachmentContainer(AttachmentAssetType.WorkshopVehicle)));
      await dispatch(setSelectedTechnicianCommentsState([]));

      // Clear distance tracking
      await dispatch(setDistanceTrackingUserPausedStoreAsync(false));
      await dispatch(setDistanceTrackingUserActivatedStoreAsync(false));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

/// SLICE

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setIsInActionState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setIsInActionState: ${action.payload}`);
      state.isInAction = action.payload;
    },
    setIsLoadingCountState: (state, action: PayloadAction<number>) => {
      const count = state.isLoadingCount + action.payload;
      logConsole(LogType.Info, `[app] setIsLoadingCountState: ${count}`);
      state.isLoadingCount = count;
    },
    setIsDebugState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setIsDebugState: ${action.payload}`);
      state.isDebug = action.payload;
      setIsDebug(action.payload);
    },
    resetClockingFetchCooldownState: (state, action: PayloadAction<void>) => {
      logConsole(LogType.Info, '[app] resetClockingFetchCooldownState');
      state.clockingFetchCooldown = 0;
    },
    addClockingFetchCooldownState: (state, action: PayloadAction<void>) => {
      logConsole(LogType.Info, '[app] addClockingFetchCooldownState');
      state.clockingFetchCooldown += 1;
    },
    subtractClockingFetchCooldownState: (state, action: PayloadAction<void>) => {
      logConsole(LogType.Info, '[app] subtractClockingFetchCooldownState');
      state.clockingFetchCooldown -= 1;
    },
    setAllowFetchClockingsState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setAllowFetchClockingsState: ${action.payload}`);
      state.allowFetchClockings = action.payload;
    },
    setAllowOverrideLabourState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setAllowOverrideLabourState: ${action.payload}`);
      state.allowOverrideLabour = action.payload;
    },
    setAllowOverrideAttendanceState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setAllowOverrideAttendanceState: ${action.payload}`);
      state.allowOverrideAttendance = action.payload;
    },
    setNetworkConnectedState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setNetworkConnectedState: ${action.payload}`);
      state.networkConnected = action.payload;
      setNetworkConnected(action.payload);
    },
    setIsBackgroundState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setIsBackgroundState: ${action.payload}`);
      state.isBackground = action.payload;
      setIsBackground(action.payload);
    },
    setStateIdState: (state, action: PayloadAction<string>) => {
      logConsole(LogType.Info, '[app] setStateIdState');
      state.stateId = action.payload;
    },
    setIsDemoState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setIsDemoState: ${action.payload}`);
      state.isDemo = action.payload;
    },
    setDemoDateTime: (state, action: PayloadAction<string>) => {
      logConsole(LogType.Info, '[app] setDemoDateTime');
      state.demoDateTime = action.payload;
    },
    setShowMainMenu: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[app] setShowMainMenu: ${action.payload}`);
      state.showMainMenu = action.payload;
    },
  },
});

/// REDUCERS

export const {
  setIsInActionState,
  setIsLoadingCountState,
  setIsDebugState,
  resetClockingFetchCooldownState,
  addClockingFetchCooldownState,
  subtractClockingFetchCooldownState,
  setAllowFetchClockingsState,
  setAllowOverrideLabourState,
  setAllowOverrideAttendanceState,
  setNetworkConnectedState,
  setIsBackgroundState,
  setStateIdState,
  setIsDemoState,
  setDemoDateTime,
  setShowMainMenu,
} = appSlice.actions;

/// CUSTOM SELECTORS

export const selectDemoDateTime = (state: RootState) => {
  if (state.app.isDemo) {
    return getStateStringAsDate(state.app.demoDateTime);
  }
  return new Date();
};

export default appSlice.reducer;
